import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";

import type { DayRange } from "../utils/dayAxis";
import type { ReportPeriod, WindowPeriod } from "../utils/period";

// --- Google report types (GET /api/google/report/<clientId>) ---

export interface GbpTotals {
  impressionsMaps: number;
  impressionsSearch: number;
  impressions: number;
  callClicks: number;
  websiteClicks: number;
  directionRequests: number;
  conversations: number;
  bookings: number;
  foodOrders: number;
  foodMenuClicks: number;
  /** callClicks + websiteClicks + directionRequests + conversations +
   *  bookings + foodOrders. */
  actions: number;
}

export interface Ga4Totals {
  sessions: number;
  engagedSessions: number;
  /** Null when the month has no ga4_monthly_totals row: users do not add
   *  across daily rows. */
  totalUsers: number | null;
  newUsers: number | null;
  keyEvents: number;
  /** engagedSessions / sessions, a ratio. */
  engagementRate: number | null;
  /** Sessions and key events from the paid channel groups. */
  paidSessions: number;
  paidKeyEvents: number;
}

export interface AdsTotals {
  impressions: number;
  clicks: number;
  costMinor: number;
  conversions: number;
  conversionsValue: number;
  phoneCalls: number;
  /** clicks / impressions, a ratio. */
  ctr: number | null;
  cpcMinor: number | null;
}

/** A month in progress against the same days of the month before: day 1
 *  to `through` (the source's settled day) and day 1 to `previousThrough`,
 *  summed from the daily rows; null = no rows in that window. GA4 users
 *  stay null: they do not add across days. */
export interface GoogleToDate<T> {
  through: string;
  previousThrough: string;
  current: T | null;
  previous: T | null;
}

/** Running total of the source's headline metric (Business Profile
 *  actions, GA4 sessions, Ads clicks) by day, each point dated: `current`
 *  through the settled day only, `previous` for the whole month (or
 *  window) before. */
export interface GooglePace {
  current: Array<{ date: string; value: number }>;
  previous: Array<{ date: string; value: number }>;
}

/** Shared by the three sources. `current`/`previous` null = no rows in
 *  that month (a gap, never zeros). `complete` = the source cursor has
 *  passed the month's last day plus its settle days. `linked` false = the
 *  client no longer names this account, so the rows are its history and no
 *  new sync will come. */
interface GoogleSourceMonth<T> {
  linked: boolean;
  /** The night the sync last ran, not the newest data day. */
  fetchedThrough: string | null;
  complete: boolean;
  /** ISO date this source's month becomes final. */
  finalFrom: string;
  /** The month, and the month before. */
  span: DayRange;
  previousSpan: DayRange;
  current: T | null;
  previous: T | null;
  /** Null once complete, or when the settled day is before the 1st. */
  toDate: GoogleToDate<T> | null;
  pace: GooglePace;
}

export interface GoogleBusinessProfileReport extends GoogleSourceMonth<GbpTotals> {
  locationId: string;
  daily: Array<{ day: string; impressions: number; actions: number }>;
  keywords: Array<{ keyword: string; impressions: number | null; belowThreshold: boolean }>;
}

export interface GoogleWebsiteReport extends GoogleSourceMonth<Ga4Totals> {
  propertyId: string;
  channels: Array<{ channelGroup: string; sessions: number; keyEvents: number }>;
  keyEvents: Array<{ eventName: string; keyEvents: number }>;
}

export interface GoogleAdsCampaignRow {
  campaignId: string;
  name: string;
  /** Null when the campaign has no reported-campaign row. */
  status: string | null;
  /** Google's channel: "SEARCH", "PERFORMANCE_MAX"; null as status. */
  channelType: string | null;
  /** "YYYY-MM-DD"; null as status. */
  startDate: string | null;
  /** Null while it runs, or as status. */
  endDate: string | null;
  impressions: number;
  clicks: number;
  costMinor: number;
  conversions: number;
  phoneCalls: number;
  /** Clicks per day of the month, a day without a row left out (a gap,
   *  never a zero). */
  daily: Array<{ date: string; clicks: number }>;
}

export interface GoogleAdsReport extends GoogleSourceMonth<AdsTotals> {
  customerId: string;
  campaigns: GoogleAdsCampaignRow[];
}

/** A null source is not linked to the client. */
export interface GoogleReport {
  year: number;
  month: number;
  businessProfile: GoogleBusinessProfileReport | null;
  website: GoogleWebsiteReport | null;
  googleAds: GoogleAdsReport | null;
}

/** One source over a window ending on its own settled day, beside the
 *  window of the same length before it. `span` and `previousSpan` are null
 *  while the source was never fetched. `compares` is false when the window
 *  before starts before `dataFrom`, the first day the data covers: then
 *  `previous` is null and `pace.previous` empty, never a part (DD-021). */
export interface GoogleSourceWindow<T> {
  linked: boolean;
  fetchedThrough: string | null;
  span: DayRange | null;
  previousSpan: DayRange | null;
  dataFrom: string;
  compares: boolean;
  current: T | null;
  previous: T | null;
  pace: GooglePace;
}

/** The Google report's window form: no search keywords and no GA4 users,
 *  both monthly. A null source is not linked to the client. */
export interface GoogleWindowReport {
  period: WindowPeriod;
  businessProfile:
    | (GoogleSourceWindow<GbpTotals> & { locationId: string; daily: GoogleBusinessProfileReport["daily"] })
    | null;
  website:
    | (GoogleSourceWindow<Ga4Totals> & {
        propertyId: string;
        channels: GoogleWebsiteReport["channels"];
        keyEvents: GoogleWebsiteReport["keyEvents"];
      })
    | null;
  googleAds: (GoogleSourceWindow<AdsTotals> & { customerId: string; campaigns: GoogleAdsCampaignRow[] }) | null;
}

export type GoogleReportData =
  | { kind: "month"; report: GoogleReport }
  | { kind: "window"; report: GoogleWindowReport };

export const useGoogleReport = (clientId: string, period: ReportPeriod, today: string) => {
  // Cached per client and month, or per client, window and day, so the
  // three Google panels, in the Reporting rail and in tiles, share one
  // request.
  const query = useQuery({
    queryKey:
      period.kind === "month"
        ? ["google-report", clientId, period.year, period.month]
        : ["google-report", clientId, period.period, today],
    queryFn: async (): Promise<GoogleReportData> => {
      const path = `/api/google/report/${clientId}`;
      if (period.kind === "month") {
        const params = { year: period.year, month: period.month };
        return { kind: "month", report: await apiClient.get<GoogleReport>(path, { params }) };
      }
      const params = { period: period.period };
      return { kind: "window", report: await apiClient.get<GoogleWindowReport>(path, { params }) };
    },
    enabled: Boolean(clientId),
  });
  const apiErr = query.error as { response?: { data?: { error?: string } }; message?: string } | null;
  const error = apiErr ? apiErr.response?.data?.error || apiErr.message || "Failed to load Google report" : null;

  return { data: query.data ?? null, isLoading: query.isLoading, error };
};
